import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';
import { StackChild, StackContainer } from 'inquiry/components/StackBase';
import UiComponentView from 'inquiry/components/UiComponentView';
import { useApply } from 'inquiry/lib/jsonLogic';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { colorCss } from 'lib/inquiryStyles';
import { ClickableStackComponent, ParentComponentProps } from 'lib/interfaces/ui-components';
import { captureException } from 'lib/reporting';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

// Extend HorizontalStack's container to add interaction states, since it is clickable
const Container = styled(StackContainer)<{
  styles: ClickableStackComponent['styles'];
  /**
   * true when loading state is from clicking this button.
   * Used to distinguish disabled vs submitting.
   */
  isPending: boolean;
}>`
  // Override the global button align-items with the default value
  // NOTE: The intended effect is for Column's to stretch, allowing their own
  // justify-content property to align their children meaningfully.
  align-items: initial;

  cursor: pointer;
  transition-property: background-color, border-color;
  transition-duration: ${TransitionDuration.Fast};

  &:hover:enabled,
  &:focus:enabled {
    background-color: ${(props) => colorCss(props.styles?.['background-color'].base.focused)};
    border-color: ${(props) => colorCss(props.styles?.['border-color'].base.focused)};
  }

  &:active:enabled {
    background-color: ${(props) => colorCss(props.styles?.['background-color'].base.active)};
    border-color: ${(props) => colorCss(props.styles?.['border-color'].base.active)};
  }

  &:disabled {
    cursor: not-allowed;

    background-color: ${(props) =>
      colorCss(
        props.isPending
          ? props.styles?.['background-color'].base.active
          : props.styles?.['background-color'].base.disabled,
      )};

    border-color: ${(props) =>
      colorCss(
        props.isPending
          ? props.styles?.['border-color'].base.active
          : props.styles?.['border-color'].base.disabled,
      )};
  }
`;

interface ClickableStackProps extends ParentComponentProps {
  component: ClickableStackComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

/**
 * A clickable version of HorizontalStack
 */
const ClickableStack = ({ component, ...props }: ClickableStackProps) => {
  const { disabled, hidden, 'transition-behavior': transitionBehavior } = component.attributes;
  const { actionsApi, inquiry, inquiryLoading } = props;

  const [t] = useTranslation();

  const {
    loading,
    error,
    callback: handleSubmit,
  } = useApiCallback(
    // Prevent HTML form submit + page refresh on click
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (transitionBehavior === 'action') {
        await actionsApi.onActionButtonClick(component.name);
      } else {
        await actionsApi.onSubmit(component.name);
      }
    },
    [actionsApi.onSubmit, component.name],
  );

  useApiErrorHandler({
    error,
    onNetworkError: () => actionsApi.showMessage(t('errors.network')),
    onError: (err) => {
      if (err instanceof ApiResponseError && err.status === 422) {
        // TODO: This should be translated too.
        actionsApi.showMessage('Invalid configuration. Please check your parameters.');
      } else {
        captureException(err);
      }
    },
  });

  const apply = useApply(component.name, inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  // WARNING: This will currently misbehave if form components are placed in a
  // hidden stack. This is because the server will still attempt to validate
  // those components.
  if (isHidden) {
    return null;
  }

  // Note: it's best practice to ensure the component that disables the button
  // is required. So if a user manually submits via devtools or something, the
  // transition will still fail. Without this, the button disable is a purely
  // UI level thing and can be trivially bypassed.
  const isDisabled = loading || inquiryLoading || apply<boolean>(disabled);

  return (
    <Container
      as='button'
      id='clickable__stack'
      styles={component.styles}
      type={transitionBehavior === 'action' ? 'button' : 'submit'}
      onClick={handleSubmit}
      disabled={isDisabled}
      isPending={loading}
    >
      {!props.isPreview &&
        component.attributes.children.map((childComponent, index) => (
          <StackChild key={childComponent.name} styles={component.styles} index={index}>
            <UiComponentView {...props} component={childComponent} />
          </StackChild>
        ))}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {props.isPreview && props.childPreviews.map((childPreview) => childPreview.component)}
    </Container>
  );
};

export default ClickableStack;
